import React, { useState, useEffect } from 'react';
import { reportsApi } from './api/reports';
import { formatDate, formatIndianCurrency } from '../sales-pipeline/utils/formatters';
import { activityLogApi } from '../sales-pipeline/api/activity-log';
import DealsActivityModal from "./DealsActivityModal";
import { getReasonLabel } from '../../config/deal-status-reasons-master';

const DealsLost = () => {

    const [filters, setFilters] = useState({
        division: '',
        zone: '',
        cluster: '',
        salesperson: '',
        from_date: '',
        to_date: ''
    });

    const [filterOptions, setFilterOptions] = useState({
        divisions: [],
        zones: [],
        clusters: [],
        salespeople: [],
        access_level: null
    });

    const [reportData, setReportData] = useState([]);

    const [pagination, setPagination] = useState({
        page: 1,
        page_size: 20
    });

    const [paginationMeta, setPaginationMeta] = useState({
        total_items: 0,
        total_pages: 0,
        has_previous: false,
        has_next: false
    });

    const [activityLogs, setActivityLogs] = useState([]);
    const [isLoadingActivity, setIsLoadingActivity] = useState(false);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [selectedDealId, setSelectedDealId] = useState(null);

    const [loadingOptions, setLoadingOptions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchFilterOptions = async () => {
            setLoadingOptions(true);
            try {
                const options = await reportsApi.getFilterOptions({
                    division: filters.division,
                    zone: filters.zone,
                    cluster: filters.cluster
                });

                setFilterOptions(options);

                setFilters(prev => {
                    const newFilters = { ...prev };

                    if (options.divisions.length === 1 && !prev.division) {
                        newFilters.division = options.divisions[0].code;
                    }

                    if (options.zones.length === 1 && !prev.zone) {
                        newFilters.zone = options.zones[0].code;
                    }

                    if (options.clusters.length === 1 && !prev.cluster) {
                        newFilters.cluster = options.clusters[0].code;
                    }

                    if (options.salespeople.length === 1 && !prev.salesperson) {
                        newFilters.salesperson = options.salespeople[0].ECode;
                    }

                    return newFilters;
                });
            } catch (err) {
                console.error('Error fetching filter options:', err);
                setError('Failed to load filter options');
            } finally {
                setLoadingOptions(false);
            }
        };
        fetchFilterOptions();
    }, [filters.division, filters.zone, filters.cluster]);


    // Fetch Deals Lost
    useEffect(() => {
        const fetchDealsLost = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await reportsApi.getDealsLostReport({
                    ...filters,
                    page: pagination.page,
                    per_page: pagination.page_size
                });

                setReportData(response.items || []);

                setPaginationMeta({
                    total_items: response.total_items,
                    total_pages: response.total_pages,
                    has_previous: response.has_previous,
                    has_next: response.has_next
                });
            } catch (err) {
                console.error("Deals Lost fetch error:", err);
                setError("Failed to load Deals Lost report");
            } finally {
                setLoading(false);
            }
        };
        fetchDealsLost();
    }, [filters, pagination]);


    const handleFilterChange = (field, value) => {
        setFilters(prev => {
            const newFilters = { ...prev, [field]: value };

            if (field === 'division') {
                newFilters.zone = '';
                newFilters.cluster = '';
                newFilters.salesperson = '';
            }

            if (field === 'zone') {
                newFilters.cluster = '';
                newFilters.salesperson = '';
            }

            if (field === 'cluster') {
                newFilters.salesperson = '';
            }

            return newFilters;
        });
        setPagination(prev => ({
            ...prev,
            page: 1
        }));
    };


    const handleDateChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const startItem = paginationMeta.total_items === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
    const endItem = Math.min(pagination.page * pagination.page_size, paginationMeta.total_items);

    const handlePageChange = (newPage) => {
        setPagination(prev => ({
            ...prev,
            page: newPage
        }));
    };

    const handlePageSizeChange = (size) => {
        setPagination(prev => ({
            ...prev,
            page_size: size,
            page: 1
        }));
    };

    const fetchActivityLogs = async (dealId) => {
        try {
            setIsLoadingActivity(true);
            setSelectedDealId(dealId);

            const logs = await activityLogApi.getActivityLogs({ dealId });
            setActivityLogs(logs || []);
            setShowActivityModal(true);

        } catch (err) {
            console.error("Error fetching activity logs:", err);
        } finally {
            setIsLoadingActivity(false);
        }
    };

    return (
        <div className="p-6 h-full overflow-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Division</label>
                        <select
                            value={filters.division}
                            onChange={(e) => handleFilterChange('division', e.target.value)}
                            disabled={loadingOptions}
                            className="w-full px-3 py-2 border rounded-md"
                        >
                            {filterOptions.access_level === 'A' && <option value="">All Divisions</option>}
                            {filterOptions.divisions.map(div => (
                                <option key={div.code} value={div.code}>{div.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Zone</label>
                        <select
                            value={filters.zone}
                            onChange={(e) => handleFilterChange('zone', e.target.value)}
                            disabled={loadingOptions}
                            className="w-full px-3 py-2 border rounded-md"
                        >
                            <option value="">All Zones</option>
                            {filterOptions.zones.map(zone => (
                                <option key={zone.code} value={zone.code}>{zone.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cluster</label>
                        <select
                            value={filters.cluster}
                            onChange={(e) => handleFilterChange('cluster', e.target.value)}
                            disabled={loadingOptions}
                            className="w-full px-3 py-2 border rounded-md"
                        >
                            <option value="">All Clusters</option>
                            {filterOptions.clusters.map(cluster => (
                                <option key={cluster.code} value={cluster.code}>{cluster.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Salesperson</label>
                        <select
                            value={filters.salesperson}
                            onChange={(e) => handleFilterChange('salesperson', e.target.value)}
                            disabled={loadingOptions}
                            className="w-full px-3 py-2 border rounded-md"
                        >
                            <option value="">All Salespersons</option>
                            {filterOptions.salespeople.map(person => (
                                <option key={person.ECode} value={person.ECode}>
                                    {person.Name} ({person.ECode})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">From Lost Date</label>
                        <input
                            type="date"
                            value={filters.from_date}
                            onChange={(e) => handleDateChange('from_date', e.target.value)}
                            className="w-full px-3 py-2 border rounded-md"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">To Lost Date</label>
                        <input
                            type="date"
                            value={filters.to_date}
                            onChange={(e) => handleDateChange('to_date', e.target.value)}
                            className="w-full px-3 py-2 border rounded-md"
                        />
                    </div>
                </div>
            </div>

            {loadingOptions && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                    <p className="text-blue-800">Loading filter options...</p>
                </div>
            )}

            {loading && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                    <p className="text-blue-800">Loading report...</p>
                </div>
            )}

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                    <p className="text-red-800">{error}</p>
                </div>
            )}

            {!loading && reportData.length === 0 && !error && (
                <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                    <div className="text-gray-400 text-5xl mb-4">📊</div>
                    <p className="text-gray-600 font-semibold text-lg">
                        No Deals Lost
                    </p>
                    <p className="text-gray-400 mt-2 text-sm">
                        No deals match the selected filters.
                    </p>
                </div>
            )}

            {/* Table */}
            {reportData.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b">
                        <h3 className="text-lg font-semibold text-gray-900">Deals Lost</h3>
                    </div>

                    <div className="overflow-auto max-h-[500px]">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs uppercase">Account</th>
                                    <th className="px-6 py-3 text-left text-xs uppercase">Contact</th>
                                    <th className="px-6 py-3 text-left text-xs uppercase">Salesperson</th>
                                    <th className="px-6 py-3 text-left text-xs uppercase">Division</th>
                                    <th className="px-6 py-3 text-left text-xs uppercase">Service</th>
                                    <th className="px-6 py-3 text-left text-xs uppercase">Deal Value</th>
                                    <th className="px-6 py-3 text-left text-xs uppercase">Lead Source</th>
                                    <th className="px-6 py-3 text-left text-xs uppercase">Deal Lost Date</th>
                                    <th className="px-6 py-3 text-left text-xs uppercase">Cycle Time</th>
                                    <th className="px-6 py-3 text-left text-xs uppercase">Deal Lost Reason</th>
                                    <th className="px-6 py-3 text-left text-xs uppercase">Actions</th>
                                </tr>
                            </thead>

                            <tbody className="bg-white divide-y divide-gray-200">
                                {reportData.map((deal, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm">{deal.Account}</td>
                                        <td className="px-6 py-4 text-sm">{deal.Contact}</td>
                                        <td className="px-6 py-4 text-sm">{deal.Salesperson}</td>
                                        <td className="px-6 py-4 text-sm">{deal.Division}</td>
                                        <td className="px-6 py-4 text-sm">{deal.Service}</td>
                                        <td className="px-6 py-4 text-sm font-medium">
                                            {formatIndianCurrency(deal.EstimatedAnnualValue)}
                                        </td>
                                        <td className="px-6 py-4 text-sm">{deal.LeadSource}</td>
                                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                                            {formatDate(deal.DealLostDate)}
                                        </td>
                                        <td className="px-6 py-4 text-sm">{deal.CycleTime}</td>
                                        <td className="px-6 py-4 text-sm">
                                            {getReasonLabel("DEAL_LOST", deal.DealLostReason)}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            <button
                                                onClick={() => fetchActivityLogs(deal.DealID)}
                                                className="flex items-center justify-center w-8 h-8 rounded-md bg-blue-100 hover:bg-blue-200 text-blue-600 transition-colors"
                                                title="View Activity Log"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between px-4 py-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                    {paginationMeta.total_items === 0
                        ? "No results"
                        : `Showing ${startItem} to ${endItem} of ${paginationMeta.total_items} results`
                    }
                </div>

                <div className="flex items-center space-x-6">
                    <div className="flex items-center space-x-2">
                        <span className="text-sm">Rows</span>
                        <select
                            value={pagination.page_size}
                            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                            className="border rounded px-2 py-1 text-sm"
                        >
                            {[10, 20, 30, 40, 50].map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                    </div>

                    <div className="text-sm">
                        Page {pagination.page} of {paginationMeta.total_pages || 1}
                    </div>

                    <div className="flex space-x-2">
                        <button
                            className="px-2 py-1 border rounded disabled:opacity-50"
                            onClick={() => handlePageChange(1)}
                            disabled={!paginationMeta.has_previous}
                        >
                            «
                        </button>

                        <button
                            className="px-2 py-1 border rounded disabled:opacity-50"
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={!paginationMeta.has_previous}
                        >
                            ‹
                        </button>

                        <button
                            className="px-2 py-1 border rounded disabled:opacity-50"
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={!paginationMeta.has_next}
                        >
                            ›
                        </button>

                        <button
                            className="px-2 py-1 border rounded disabled:opacity-50"
                            onClick={() => handlePageChange(paginationMeta.total_pages)}
                            disabled={!paginationMeta.has_next}
                        >
                            »
                        </button>
                    </div>
                </div>
            </div>

            <DealsActivityModal
                show={showActivityModal}
                activityLogs={activityLogs}
                isLoading={isLoadingActivity}
                onClose={() => setShowActivityModal(false)}
            />
        </div>
    );
};

export default DealsLost;